import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { useAudio } from './DassPage';
import { getOfflineAssessment } from '../utils/offlineAssessment';
import { adaptStarScoresForCompletion } from '../logic/insights/starAdapters';
import StarRadarChart from '../components/StarRadarChart';
import mobileBg from '../assets/welness-dass-wallpaper.png';
import desktopBg from '../assets/welness-dass-walpaper-desktop.png';


interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface QuestionData {
  id: string;
  question: string;
  category: string;
  attribute: string;
  tips: string;
}

const StarOfWellbeingCompletion: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isAuthenticated } = useAuth();
  const [scores, setScores] = useState<{ [key: string]: number }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [chartMode, setChartMode] = useState<'filled' | 'slashed'>('filled');
  const [isFromOfflineCache, setIsFromOfflineCache] = useState<boolean>(false);
  const [showAssessmentCta, setShowAssessmentCta] = useState<boolean>(false);

  // Determine if this is the public version
  const isPublicVersion = location.pathname.startsWith('/star-of-well-being');

  // Use shared audio context
  const { isPlaying, volume, isMuted, togglePlayPause, toggleMute, setVolume } = useAudio();

  // Questions data for mapping responses
  const questions: QuestionData[] = useMemo(() => [
    {
      id: '1',
      question: 'How connected do you feel to your values, purpose, or spiritual beliefs?',
      category: 'inherent',
      attribute: 'Spiritual',
      tips: 'Consider whether you feel guided by a deeper sense of meaning, inner peace, or moral compass in daily life.'
    },
    {
      id: '2',
      question: 'How well do your eating habits support your health and energy needs?',
      category: 'inherent',
      attribute: 'Nutritional',
      tips: 'Think about food quality, balance, hydration, and whether you eat in a way that sustains your well-being.'
    },
    {
      id: '3',
      question: 'How would you describe your current physical health?',
      category: 'inherent',
      attribute: 'Physical',
      tips: 'Include energy levels, fitness, mobility, and absence or presence of illness or pain.'
    },
    {
      id: '4',
      question: 'How well do you manage emotions, thoughts, and decision-making?',
      category: 'inherent',
      attribute: 'Emotio-Psycho-Rational',
      tips: 'Consider emotional stability, stress coping, self-talk, and clarity in judgment.'
    },
    {
      id: '5',
      question: 'How clear and fulfilled do you feel in your life\'s purpose and legacy?',
      category: 'inherent',
      attribute: 'Legendarial',
      tips: 'Reflect on whether your actions align with your long-term goals and how you contribute beyond yourself.'
    },
    {
      id: '6',
      question: 'How well do your traditions, beliefs, and cultural identity support your well-being?',
      category: 'coherent',
      attribute: 'Religio-Traditio-Cultural',
      tips: 'Consider harmony between your personal values and the traditions or culture you engage with.'
    },
    {
      id: '7',
      question: 'How secure and confident do you feel about your financial situation?',
      category: 'coherent',
      attribute: 'Financial',
      tips: 'Include income stability, ability to meet needs, savings, and financial planning.'
    },
    {
      id: '8',
      question: 'How supportive is your physical environment for your health and well-being?',
      category: 'coherent',
      attribute: 'Environmental',
      tips: 'Think about home, workplace, access to nature, and safety in your surroundings.'
    },
    {
      id: '9',
      question: 'How strong and supportive are your personal and professional relationships?',
      category: 'coherent',
      attribute: 'Socio-Relational',
      tips: 'Consider trust, communication, and sense of belonging in your relationships.'
    },
    {
      id: '10',
      question: 'How satisfied and engaged are you in your current work or daily role?',
      category: 'coherent',
      attribute: 'Occupational',
      tips: 'Reflect on fulfillment, work-life balance, and whether your role aligns with your skills and passions.'
    }
  ], []);

  useEffect(() => {
    // Set background image based on screen size
    const updateBackgroundImage = () => {
      const isDesktop = window.innerWidth >= 1024;
      const bgImage = isDesktop ? desktopBg : mobileBg;
      document.documentElement.style.setProperty('--bg-image', `url(${bgImage})`);
    };

    // Set initial background
    updateBackgroundImage();

    // Update on resize
    window.addEventListener('resize', updateBackgroundImage);

    return () => {
      window.removeEventListener('resize', updateBackgroundImage);
    };
  }, []);

  useEffect(() => {
    // Get responses from location state or offline cache
    let responses: AssessmentResponse[] = location.state?.responses;

    if (responses && responses.length > 0) {
      // Data came from navigation state (API response or passed from assessment page)
      setIsFromOfflineCache(false);
    } else if (isPublicVersion) {
      // Public version - try to load from offline cache
      const offlineData = getOfflineAssessment('dass-star');

      if (offlineData?.responses) {
        responses = offlineData.responses;
        setIsFromOfflineCache(true);
      } else {

        navigate('/star-of-well-being');
        return;
      }
    } else {
      // Panel version without responses - redirect

      navigate('/panel/wellness/dass');
      return;
    }

    if (responses && responses.length > 0) {
      // Map responses to scores
      const scoreMap: { [key: string]: number } = {};
      let hasTwoStarScore = false;

      responses.forEach(response => {
        const question = questions.find(q => q.id === response.questionId);
        if (question) {
          scoreMap[question.attribute] = response.answer;
          // Check if any answer is two stars or less
          if (response.answer <= 2) {
            hasTwoStarScore = true;
          }
        }
      });

      setScores(scoreMap);

      // Show assessment CTA modal if any score is 2 stars
      if (hasTwoStarScore) {
        setShowAssessmentCta(true);
      }
    } else {
      // Fallback: try to get from localStorage or redirect

      navigate('/panel/learn/');
      return;
    }

    setIsLoading(false);
  }, [location.state, navigate, questions, isPublicVersion]);


  const handleFinishAssessment = () => {
    // Navigate back to wellness panel
    navigate('/panel/wellness');
  };

  const getScoreInsights = (attribute: string, score: number) => {
    const insights = {
      'Spiritual': {
        1: "You may be feeling disconnected from your purpose or values. Consider mindfulness, spiritual counseling, or reflective journaling to realign.",
        2: "You're exploring your inner values but still building consistency. Try guided meditation or joining a purpose-driven community.",
        3: "You have a steady sense of purpose. Maintain with regular reflection and service to others.",
        4: "Your purpose is a guiding light. Continue to inspire and mentor others in their journey."
      },
      'Nutritional': {
        1: "Your eating habits may be affecting your energy and mood. Begin with small changes like balanced meals and hydration.",
        2: "You're improving your diet but could benefit from a structured plan. Consider personalized nutrition guidance.",
        3: "Your nutrition is strong, supporting daily performance. Maintain by adapting to your body's changing needs.",
        4: "Excellent nutritional habits. Share your practices with family or colleagues to inspire healthier choices."
      },
      'Physical': {
        1: "Significant physical health challenges.",
        2: "Actively working on physical wellness.",
        3: "Consistent physical health maintenance.",
        4: "Excellent physical health and vitality."
      },
      'Emotio-Psycho-Rational': {
        1: "Significant emotional and mental challenges.",
        2: "Actively developing emotional management skills.",
        3: "Consistent emotional intelligence and clear thinking.",
        4: "Exceptional emotional and mental well-being."
      },
      'Legendarial': {
        1: "Unclear sense of purpose and legacy.",
        2: "Actively exploring personal purpose.",
        3: "Consistent alignment with life purpose.",
        4: "Clear, impactful life purpose and legacy."
      },
      'Religio-Traditio-Cultural': {
        1: "Significant cultural and traditional disconnection.",
        2: "Actively exploring cultural identity.",
        3: "Consistent cultural and traditional alignment.",
        4: "Deep, meaningful cultural connection."
      },
      'Financial': {
        1: "Significant financial challenges.",
        2: "Actively improving financial situation.",
        3: "Consistent financial stability.",
        4: "Excellent financial security and planning."
      },
      'Environmental': {
        1: "Challenging physical environment.",
        2: "Actively improving environmental conditions.",
        3: "Consistent supportive environment.",
        4: "Optimal, nurturing physical environment."
      },
      'Socio-Relational': {
        1: "Significant relationship challenges.",
        2: "Actively developing relationship skills.",
        3: "Consistent strong relationships.",
        4: "Exceptional relational well-being."
      },
      'Occupational': {
        1: "Significant occupational challenges.",
        2: "Actively improving work satisfaction.",
        3: "Consistent work fulfillment.",
        4: "Exceptional occupational well-being."
      }
    };

    return insights[attribute as keyof typeof insights]?.[score as keyof typeof insights[keyof typeof insights]] || '';
  };

  if (isLoading) {
    return (
      <div className="completion-page">
        <div className="completion-page__loading">
          <div className="completion-page__spinner"></div>
          <p>Loading your results...</p>
        </div>
      </div>
    );
  }



  return (
    <div className="completion-page">
      {/* Header */}
      <div className="completion-page__header">
      </div>

      {/* Content */}
      <div className="completion-page__content">
        <h1 className="completion-page__title">Your Star of Well-Being Results</h1>

        {/* Offline Data Notice */}
        {isFromOfflineCache && (
          <div className="completion-page__status completion-page__status--warning">
            📄 Your assessment results are stored locally and will be synced to your account when you log in.
          </div>
        )}

        {/* Radar Chart */}
        <div className="completion-page__chart-section">
          <StarRadarChart scores={scores} chartMode={chartMode} size="full" />
        </div>

        {/* Chart Mode Toggle */}
        <div className="completion-page__chart-controls">
          <div className="completion-page__mode-toggle">
            <button
              className={`completion-page__mode-btn ${chartMode === 'filled' ? 'active' : ''}`}
              onClick={() => setChartMode('filled')}
            >
              Filled
            </button>
            <button
              className={`completion-page__mode-btn ${chartMode === 'slashed' ? 'active' : ''}`}
              onClick={() => setChartMode('slashed')}
            >
              Slashed
            </button>
          </div>
        </div>


        {/* Actions */}
        <div className="completion-page__actions">
          <button
            className="completion-page__btn completion-page__btn--primary"
            onClick={handleFinishAssessment}
          >
            Complete Finish Assessment
          </button>
          <button
            className="completion-page__btn completion-page__btn--secondary"
            onClick={() => navigate('/panel/wellness/dass')}
          >
            Take Assessment Again
          </button>
        </div>
      </div>

      {/* Assessment CTA Modal */}
      {showAssessmentCta && (
        <div className="completion-page__modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="cta-title">
          <div className="completion-page__modal">
            <div className="completion-page__modal-content">
              <h2 id="cta-title" className="completion-page__modal-title">
                Assessment Recommendation
              </h2>
              <p className="completion-page__modal-message">
                Based on your assessment scores, we recommend you to take the Mental Health Assessment to gain deeper insights into your emotional well-being.
              </p>
              <div className="completion-page__modal-actions">
                <button
                  className="completion-page__modal-btn completion-page__modal-btn--primary"
                  onClick={() => {
                    setShowAssessmentCta(false);
                    if (!isAuthenticated) {
                      navigate('/mentalhealth');
                    } else {
                      navigate('/panel/learn/emotio-psycho-rational-assessment', { state: { dass21: true } });
                    }
                  }}
                >
                  Take
                </button>
                <button
                  className="completion-page__modal-btn completion-page__modal-btn--secondary"
                  onClick={() => setShowAssessmentCta(false)}
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StarOfWellbeingCompletion;



